import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  RoundCreationSettingsResponse,
  RoundCreationSettingsUpdate,
  RoundRebuildRequest,
} from '../../../data-models/round-creation-settings.data-model';
import {
  getRoundCreationSettings,
  getRoundSecurityTypes,
  rebuildRounds,
  updateRoundCreationSettings,
} from '../../../services/queries/MaggieRoundCreationSettingsQueries';

export const ROUND_CREATION_SETTINGS_QUERY_KEY = ['round-creation-settings'] as const;

/**
 * Reads and writes go through the guarded settings endpoints, never through preferences: those
 * writes are unpermissioned and would defeat the CS-only gate this page relies on.
 */
export function useRoundCreationConfigActions() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ROUND_CREATION_SETTINGS_QUERY_KEY,
    queryFn: getRoundCreationSettings,
    staleTime: 0,
  });

  const setCached = useCallback(
    (settings: RoundCreationSettingsResponse) =>
      queryClient.setQueryData(ROUND_CREATION_SETTINGS_QUERY_KEY, settings),
    [queryClient]
  );

  const save = useMutation({
    mutationFn: (payload: RoundCreationSettingsUpdate) => updateRoundCreationSettings(payload),
    onSuccess: setCached,
  });

  // The rebuild saves first, so the settings row is stale afterwards even when the apply fails midway.
  const rebuild = useMutation({
    mutationFn: (payload: RoundRebuildRequest) => rebuildRounds(payload),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ROUND_CREATION_SETTINGS_QUERY_KEY }),
  });

  return {
    settings: query.data ?? null,
    isLoading: query.isLoading,
    loadError: query.error,
    saveConfig: save.mutateAsync,
    rebuild: rebuild.mutateAsync,
    isRebuilding: rebuild.isPending,
  };
}

/**
 * The securities the engine can recognise. Served by the API rather than listed here, so the
 * options cannot drift from what actually decides whether a transaction may create a round.
 */
export function useRoundSecurityTypes() {
  return useQuery({
    queryKey: ['round-security-types'],
    queryFn: getRoundSecurityTypes,
    staleTime: Infinity,
  });
}
